import { loadScript } from './helpers';
import { config } from 'src/utils/config';

let gaLoadPromise: Promise<unknown>;
let clarityLoadPromise: Promise<unknown>;

export function areCookiesEnabled() {
  return config.value.analytics?.enabled;
}

function getGaId() {
  return config.value.analytics?.gaId || env.GA_ID;
}

function getClarityId() {
  return config.value.analytics?.clarityId || env.CLARITY_ID;
}

export async function loadAnalytics() {
  if (!areCookiesEnabled()) {
    return;
  }

  const gaId = getGaId();
  if (!gaId) {
    return;
  }

  gaLoadPromise = loadScript(`//www.googletagmanager.com/gtag/js?id=${gaId}`, {
    attributes: { async: true, defer: true },
    id: 'gtag-script',
    additionalScript: `window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} window.gtag = gtag`,
  });

  await gaLoadPromise;

  sendAnalytics('consent', 'update', {
    ad_personalization: 'granted',
    ad_user_data: 'granted',
    ad_storage: 'granted',
    analytics_storage: 'granted',
  });

  sendAnalytics('config', gaId, { debug_mode: false, send_to: gaId });
  sendAnalytics('set', { debug_mode: false, send_to: gaId });
}

export async function sendAnalytics(...args: Parameters<Gtag.Gtag>) {
  if (!areCookiesEnabled()) {
    return;
  }

  await gaLoadPromise;
  if (!('gtag' in window)) return;
  window.gtag.call(window, ...args);
}

export async function loadClarity() {
  if (!areCookiesEnabled()) {
    return;
  }

  const clarityId = getClarityId();
  if (!clarityId) {
    return;
  }

  clarityLoadPromise = new Promise<void>((resolve, reject) => {
    (function (
      c: Record<string, Record<'q', unknown[]> & ((...args: string[]) => void)>,
      l: Document,
      a: string,
      r: string,
      i: string,
      t?: HTMLScriptElement,
      y?: Node
    ): void {
      c[a] =
        c[a] ||
        function (...args: string[]) {
          // arguments is not overwritten so safe to disable
          // eslint-disable-next-line prefer-rest-params
          (c[a].q = c[a].q || []).push(args);
        };
      t = l.createElement(r) as HTMLScriptElement;
      t.async = true;
      t.src = 'https://www.clarity.ms/tag/' + i;
      t.addEventListener('load', () => resolve());
      t.addEventListener('error', reject);
      y = l.getElementsByTagName(r)[0];
      y?.parentNode?.insertBefore(t, y);
      const clarity = getClarity();
      if (clarity) {
        if (config.value.currentUserId) {
          window.clarity('set', 'userId', config.value.currentUserId);
        }
        if (config.value.clientId) {
          window.clarity('set', 'clientId', config.value.clientId);
        }
        if (config.value.clientTheme) {
          window.clarity('set', 'clientTheme', config.value.clientTheme);
        }
      }
    })(
      window as unknown as Record<string, Record<'q', unknown[]> & ((...args: string[]) => void)>,
      document,
      'clarity',
      'script',
      clarityId
    );
  });
  await clarityLoadPromise.catch(() => {});
}

export function getClarity() {
  if (!('clarity' in window)) return;
  return window.clarity;
}

export async function loadOneTrust() {
  const oneTrustDomainId = config.value.analytics?.oneTrustDomainId;
  if (!oneTrustDomainId) {
    return;
  }
  if (window.OnetrustActiveGroups) {
    return;
  }
  if (document.querySelector('script[data-domain-script]')) {
    console.warn('OneTrust script is already loaded');
    return;
  }
  return loadScript(`https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js`, {
    attributes: {
      type: 'text/javascript',
      charset: 'UTF-8',
      'data-domain-script': `${oneTrustDomainId}`,
    },
    id: 'onetrust-script',
  });
}

export function updateCookieConsent(consent: { analytics?: boolean; functional?: boolean }) {
  if (config.value.analytics) {
    config.value.analytics.enabled = consent.analytics ?? false;
  }
  if (consent.analytics) {
    loadAnalytics();
    loadOpenReplay();
    loadClarity();
  }
}

export async function loadOpenReplay() {
  if (!areCookiesEnabled()) {
    return;
  }
  const { init } = await import('src/plugins/openreplay');
  init();
}

export const CATEGORY_ANALYTICS = 'C0002';
export const CATEGORY_FUNCTIONAL = 'C0003';

export function allowedGroups() {
  return (window.OnetrustActiveGroups || '').split(',');
}

export function onConsentReady(cb: () => void) {
  whenOneTrustReady(() => {
    if (window.OneTrust && typeof window.OneTrust.OnConsentChanged === 'function') {
      cb();
      window.OneTrust.OnConsentChanged(cb);
    } else {
      document.addEventListener('OneTrustGroupsUpdated', cb, { once: true });
    }
  });
}
export function whenOneTrustReady(callback: () => void) {
  if (window.OnetrustActiveGroups) {
    callback();
    return;
  }

  document.addEventListener('OneTrustGroupsUpdated', callback, { once: true });

  const prev = window.OptanonWrapper || (() => {});
  window.OptanonWrapper = function () {
    prev();
    callback();
  };
}
